import { useEffect, useRef } from 'react';
import type { BloxCustomDataProviderType } from '../../../utility/types/dataProvider';
import { getSiteTagName } from 'components/SiteTagItemMenu/helpers/siteTagHelpers';
import { SiteTagDomainMap } from 'providers/contexts/extensions/SiteTaxonomy';

type SiteTagFetchItem = string | { name?: string; path?: string; [key: string]: unknown };

type UseSiteTagDomainsArgs = {
  dataProvider: BloxCustomDataProviderType;
  isFocused?: boolean;
  isListReady: boolean;
  listTarget: string;
  onDomainsLoaded?: (map: SiteTagDomainMap) => void;
  items: SiteTagFetchItem[];
};

export const useSiteTagDomains = ({
  dataProvider,
  isFocused,
  isListReady,
  listTarget,
  onDomainsLoaded,
  items,
}: UseSiteTagDomainsArgs) => {
  // Track whether we've already fetched domains for the current dialog session.
  const domainsFetchedRef = useRef(false);

  // Mirror items into a ref so the fetch effect can read the current tag list
  // without listing it as a dependency (which would trigger cleanup/cancel on pagination).
  const itemsRef = useRef<SiteTagFetchItem[]>([]);
  itemsRef.current = items;

  // Cancellation token whose lifetime matches the dialog session, not the effect run.
  // Using a ref (instead of a local variable) means intermediate query-state changes
  // (e.g. isListReady flipping false to true while the user types in search) will NOT
  // cancel an already in-flight fetch, preventing race conditions.
  const isCancelledRef = useRef(false);

  // Reset fetch guard (and cancellation token) on dialog open/close.
  useEffect(() => {
    if (!isFocused) {
      domainsFetchedRef.current = false;
      isCancelledRef.current = true; // cancel any fetch still in flight from previous open
    } else {
      isCancelledRef.current = false; // new dialog session — allow the fetch to resolve
    }
  }, [isFocused]);

  // Fetch site-tag domain taxonomy once per dialog open.
  // isListReady stays in the deps so the effect fires once the list is ready, but we
  // do NOT return a cleanup that sets isCancelled — that would cancel the fetch whenever
  // isListReady changes (e.g. on search-input debounce). Cancellation is handled solely
  // by the isCancelledRef above.
  useEffect(() => {
    if (listTarget !== 'site_tags' || !isFocused || domainsFetchedRef.current) {
      return;
    }

    if (!isListReady || !itemsRef.current.length) {
      return;
    }

    domainsFetchedRef.current = true;

    const siteTagNames = Array.from(
      new Set(
        itemsRef.current
          .map(getSiteTagName)
          .filter((name): name is string => Boolean(name)),
      ),
    );

    if (!siteTagNames.length) {
      return;
    }

    const fetchSiteTagDomains = async () => {
      try {
        const result = await dataProvider.postSiteTagDomains({
          site_tag_names: siteTagNames,
        });

        if (!isCancelledRef.current) {
          onDomainsLoaded?.((result?.data ?? {}) as SiteTagDomainMap);
        }
      } catch (error) {
        if (!isCancelledRef.current) {
          console.error('[useSiteTagDomains] postSiteTagDomains error:', error);
        }
      }
    };

    fetchSiteTagDomains();
  }, [dataProvider, isFocused, isListReady, listTarget, onDomainsLoaded]);
};
